import type {
  ApplicationWithStats,
  CreateVersionProduct,
  UpdateVersionProduct,
  VersionProduct,
} from '@owl/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useConfirm } from '../components/Confirm';
import type { ShellCtx } from '../components/Shell';
import { useToast } from '../components/Toast';
import { ApiError, type SiembraRun, type SiembraState, api } from '../lib/api';
import { timeAgo } from '../lib/time';
import { useEscapeKey } from '../lib/useEscapeKey';
import { useLoader } from '../lib/useLoader';

type VersionTone = 'match' | 'drift' | 'unknown';

// Sentinel tab that shows every enabled product stacked, each with its own siembra
// zone. Kept out of the product-key namespace so a product literally named "ALL"
// can't collide with it.
const ALL_KEY = '__ALL__';

// A siembra run drives a deploy we can only observe indirectly: the GitHub Action
// pushes to the tenant repos, then Railway redeploys and the app heartbeats its new
// version. We keep the "working" spinner alive across that whole window.
const WATCH_MS = 10 * 60 * 1000; // stop watching a deploy after 10 min, drift or not
const GRACE_MS = 25 * 1000; // spin right after dispatch until the Action run shows up
const POLL_MS = 6000; // while running, refresh siembra + app versions this often

// Green when the instance runs the product's Core target, red when it lags it,
// neutral when either side is unknown (no heartbeat version yet / no target set).
function versionTone(appVersion: string | null, target: string | undefined): VersionTone {
  if (!appVersion || !target) return 'unknown';
  return appVersion === target ? 'match' : 'drift';
}

const VERSION_DOT: Record<VersionTone, string> = {
  match: '#22c55e',
  drift: '#ef4444',
  unknown: '#6b7280',
};

// The same three tones as a soft pill around the version, so a column of numbers
// reads at a glance instead of forcing the eye onto the dot.
const VERSION_TINT: Record<VersionTone, { border: string; background: string }> = {
  match: { border: 'rgba(34, 197, 94, 0.35)', background: 'rgba(34, 197, 94, 0.08)' },
  drift: { border: 'rgba(239, 68, 68, 0.35)', background: 'rgba(239, 68, 68, 0.08)' },
  unknown: { border: 'rgba(255, 255, 255, 0.1)', background: 'transparent' },
};

// A run that is still queued or in progress has no tone of its own yet: amber says
// "moving, not a verdict".
const RUN_AMBER = {
  dot: '#f59e0b',
  border: 'rgba(245, 158, 11, 0.35)',
  background: 'rgba(245, 158, 11, 0.08)',
};

const PANEL_WRAP: React.CSSProperties = {
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  padding: '10px 12px',
  marginBottom: 16,
};

// One logical application (a tenant) as shown in the board. The version table
// lives outside the global DEV/PROD toggle and only tracks production, which is
// what the siembra deploys.
type AppGroup = {
  key: string;
  name: string;
  product: string;
  company: string;
  instance: string;
  tenantRepo: string;
  app: ApplicationWithStats;
};

// Which tenants a launched siembra is expected to move, and how long to keep
// spinning while Railway catches up. null = nothing in flight. Siembra targets
// `main`, so what it moves is the production deployment.
type DeployWatch = { repos: string[]; since: number; until: number };

// Small spinning ring reused inside the siembra buttons to signal "working, wait".
function Spinner() {
  return <span className="spinner" aria-hidden />;
}

// Build a tenant's repo name from the product's pattern (tokens {product},
// {company}, {instance}). Default VERA-Norlia / VERA-CBFNavarroLavadero.
function renderTenantRepo(pattern: string, a: ApplicationWithStats): string {
  return pattern
    .replaceAll('{product}', a.product)
    .replaceAll('{company}', a.company)
    .replaceAll('{instance}', a.instance);
}

// One row per production tenant (product/company/instance). Non-production
// applications are ignored: the siembra deploys straight to production.
function buildGroups(items: ApplicationWithStats[], product: VersionProduct): AppGroup[] {
  return items
    .filter((a) => a.product === product.key && a.environment === 'production')
    .map((a) => ({
      key: `${a.product} ${a.company} ${a.instance}`,
      name: a.name,
      product: a.product,
      company: a.company,
      instance: a.instance,
      tenantRepo: renderTenantRepo(product.tenantRepoPattern, a),
      app: a,
    }))
    .sort((x, y) => x.key.localeCompare(y.key));
}

export function Versions({ ctx }: { ctx: ShellCtx }) {
  const refreshTick = ctx.refreshTick;
  const [products, setProducts] = useState<VersionProduct[] | null>(null);
  // ALL_KEY (default) shows every product at once; a product key shows just that one.
  const [activeKey, setActiveKey] = useState<string | null>(ALL_KEY);
  // The version board lives outside the global env toggle — it always shows
  // production, so we fetch every environment regardless of ctx.environment.
  const [items, setItems] = useState<ApplicationWithStats[] | null>(null);
  const [coreVersions, setCoreVersions] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [managing, setManaging] = useState(false);
  const { push } = useToast();

  const enabled = useMemo(() => (products ?? []).filter((p) => p.enabled), [products]);
  const isAll = activeKey === ALL_KEY;
  const activeProduct = enabled.find((p) => p.key === activeKey) ?? null;
  const shownProducts = isAll ? enabled : activeProduct ? [activeProduct] : [];

  const load = useCallback(async () => {
    try {
      const [productsRes, appsRes, settingsRes] = await Promise.all([
        api.listVersionProducts(),
        api.listApplications(),
        api.getSettings(),
      ]);
      setProducts(productsRes.items);
      setItems(appsRes.items);
      const cv = settingsRes.settings.core_version;
      setCoreVersions(
        cv && typeof cv === 'object' && !Array.isArray(cv) ? (cv as Record<string, string>) : {},
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  }, []);

  useLoader(load, refreshTick);

  // Default to the "Todos" tab; keep the current selection if it's still valid.
  useEffect(() => {
    if (enabled.length === 0) {
      setActiveKey(null);
      return;
    }
    setActiveKey((prev) => {
      if (prev === ALL_KEY) return prev;
      if (prev && enabled.some((p) => p.key === prev)) return prev;
      return ALL_KEY;
    });
  }, [enabled]);

  // Per-product Core target fallback, stored in the OWL settings KV under
  // `core_version`. Only used when the product's Core repo can't be read from git.
  async function saveCoreVersion(product: string, version: string) {
    const next = { ...coreVersions };
    const v = version.trim();
    if (v) next[product] = v;
    else delete next[product];
    try {
      await api.patchSettings({ core_version: next });
      setCoreVersions(next);
      push(`Core ${product} → ${v || '—'}`);
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Update failed');
    }
  }

  return (
    <div className="view-pad">
      <div className="view-head">
        <div>
          <div className="view-title">Control de versiones</div>
          <div className="view-sub">
            {enabled.length === 0
              ? 'Productos bajo control de versiones'
              : isAll
                ? `${enabled.length} producto${enabled.length === 1 ? '' : 's'} · Core vs PROD`
                : `${activeProduct?.displayName ?? ''} · Core vs PROD`}
          </div>
        </div>
        <button type="button" className="cta-secondary" onClick={() => setManaging(true)}>
          Administrar productos
        </button>
      </div>

      {managing && (
        <ProductManager
          products={products ?? []}
          onClose={() => setManaging(false)}
          onChanged={load}
        />
      )}

      {error && <div className="error-banner">{error}</div>}

      {products === null ? (
        <div className="empty-state">Loading…</div>
      ) : enabled.length === 0 ? (
        <div className="empty-state">
          No hay productos configurados. Añade uno (VERA, ARGOS…) en Settings · Control de
          versiones.
        </div>
      ) : (
        <>
          <ProductTabs products={enabled} activeKey={activeKey} onSelect={setActiveKey} />

          {items === null ? (
            <div className="empty-state">Loading…</div>
          ) : (
            shownProducts.map((p) => (
              <ProductBoard
                key={p.key}
                product={p}
                items={items}
                manualCore={coreVersions[p.key] ?? ''}
                onSaveCore={(v) => saveCoreVersion(p.key, v)}
                reloadItems={load}
                refreshTick={refreshTick}
                showHeader={isAll}
              />
            ))
          )}
        </>
      )}
    </div>
  );
}

// Segmented product selector (Todos · VERA · ARGOS · SONNAR), reusing the app's
// range-picker look so the board feels native to the rest of the UI.
function ProductTabs({
  products,
  activeKey,
  onSelect,
}: {
  products: VersionProduct[];
  activeKey: string | null;
  onSelect: (key: string) => void;
}) {
  return (
    <div className="range-picker" style={{ marginBottom: 16, width: 'fit-content' }}>
      <button
        type="button"
        className={`range-opt ${activeKey === ALL_KEY ? 'active' : ''}`}
        onClick={() => onSelect(ALL_KEY)}
      >
        Todos
      </button>
      {products.map((p) => (
        <button
          type="button"
          key={p.key}
          className={`range-opt ${activeKey === p.key ? 'active' : ''}`}
          onClick={() => onSelect(p.key)}
        >
          {p.displayName}
        </button>
      ))}
    </div>
  );
}

// A single product's self-contained board: its own siembra state, selection flow,
// Core target and version table. Rendered once per product in "Todos" mode, so each
// section carries its own siembra zone.
function ProductBoard({
  product,
  items,
  manualCore,
  onSaveCore,
  reloadItems,
  refreshTick,
  showHeader,
}: {
  product: VersionProduct;
  items: ApplicationWithStats[];
  manualCore: string;
  onSaveCore: (v: string) => void | Promise<void>;
  reloadItems: () => Promise<void> | void;
  refreshTick: number;
  showHeader: boolean;
}) {
  const { push } = useToast();
  const [siembra, setSiembra] = useState<SiembraState | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dryRun, setDryRun] = useState(false);
  const [busy, setBusy] = useState(false);
  const [watch, setWatch] = useState<DeployWatch | null>(null);

  const loadSiembra = useCallback(async () => {
    try {
      setSiembra(await api.getSiembra(product.key));
    } catch {
      setSiembra(null);
    }
  }, [product.key]);

  useLoader(loadSiembra, refreshTick);

  const groups = buildGroups(items, product);
  // The product's target comes straight from git (settings.py); on a read failure
  // it falls back to the manual per-product value in the settings KV.
  const target = siembra?.coreVersion ?? manualCore ?? undefined;

  // A tenant's derived repo must exist in the Core registry (tenants.json) or the
  // siembra Action skips it silently. `registeredTenants === null` means OWL
  // couldn't read the registry — then we can't tell, so we don't block (unknown, not
  // "unregistered"). Only registered tenants are selectable/dispatchable.
  const registeredTenants = siembra?.registeredTenants ?? null;
  const isRegistered = (repo: string) =>
    registeredTenants === null || registeredTenants.includes(repo);
  const sembrableRepos = groups.map((g) => g.tenantRepo).filter(isRegistered);
  const allSelected = sembrableRepos.length > 0 && sembrableRepos.every((r) => selected.has(r));

  // "Running" = the whole visible-progress window: the dispatch call, then the
  // Action run (queued/in_progress), then the Railway deploy — observed as the
  // watched tenants still drifting from target until they catch up or we time out.
  const now = Date.now();
  const lastRun = siembra?.runs?.[0];
  const actionInProgress = !!lastRun && lastRun.status != null && lastRun.status !== 'completed';
  const inGrace = !!watch && now < watch.since + GRACE_MS;
  const deployPending =
    !!watch &&
    now < watch.until &&
    groups.some((g) => {
      if (!watch.repos.includes(g.tenantRepo)) return false;
      return versionTone(g.app.version, target) === 'drift';
    });
  const running = busy || actionInProgress || inGrace || deployPending;

  // Once nothing is in flight, drop the watch so a later unrelated drift can't
  // reignite the spinner.
  useEffect(() => {
    if (watch && !running) setWatch(null);
  }, [running, watch]);

  // While running, keep the siembra state and the app versions fresh so the spinner
  // reacts to the real deploy and turns itself off. No polling at rest.
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => {
      loadSiembra();
      reloadItems();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [running, loadSiembra, reloadItems]);

  function reposFor(tenants: string): string[] {
    return tenants === 'all'
      ? sembrableRepos
      : tenants
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
  }

  function startWatch(tenants: string) {
    const t = Date.now();
    setWatch({ repos: reposFor(tenants), since: t, until: t + WATCH_MS });
  }

  async function trigger(tenants: string) {
    setBusy(true);
    try {
      await api.triggerSiembra(product.key, { tenants, dryRun });
      push(dryRun ? 'Siembra (dry-run) lanzada' : 'Siembra lanzada');
      // A dry-run deploys nothing, so there's nothing to watch for.
      if (!dryRun) startWatch(tenants);
      window.setTimeout(() => loadSiembra(), 1500);
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Fallo al lanzar la siembra');
    } finally {
      setBusy(false);
    }
  }

  async function toggleAuto() {
    if (!siembra) return;
    try {
      const { autoEnabled } = await api.setSiembraAuto(product.key, !siembra.autoEnabled);
      setSiembra({ ...siembra, autoEnabled });
      push(`Auto-siembra ${autoEnabled ? 'activada' : 'desactivada'}`);
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'No se pudo cambiar el switch');
    }
  }

  function toggleRow(repo: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(repo)) next.delete(repo);
      else next.add(repo);
      return next;
    });
  }

  function toggleAll() {
    setSelected(() => (allSelected ? new Set() : new Set(sembrableRepos)));
  }

  function stopSelecting() {
    setSelecting(false);
    setSelected(new Set());
  }

  return (
    <div style={{ marginBottom: showHeader ? 28 : 0 }}>
      {showHeader && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            margin: '18px 0 10px',
          }}
        >
          <div className="view-title" style={{ fontSize: 16 }}>
            {product.displayName}
          </div>
          <span className="dim" style={{ fontSize: 12 }}>
            {groups.length} aplicaci{groups.length === 1 ? 'ón' : 'ones'}
          </span>
          {running && (
            <span
              className="dim"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}
            >
              <Spinner /> En marcha…
            </span>
          )}
        </div>
      )}

      {siembra && (
        <SiembraBar
          product={product}
          siembra={siembra}
          manualValue={manualCore}
          onSaveCore={onSaveCore}
          dryRun={dryRun}
          setDryRun={setDryRun}
          busy={busy}
          running={running}
          selecting={selecting}
          selectedCount={selected.size}
          onToggleAuto={toggleAuto}
          onSembrarTodos={() => trigger('all')}
          onStartSelect={() => setSelecting(true)}
          onCancelSelect={stopSelecting}
          onSembrarSeleccion={() => trigger(Array.from(selected).join(','))}
        />
      )}

      {groups.length === 0 ? (
        <div className="empty-state">No hay aplicaciones de {product.displayName} todavía.</div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                {selecting && (
                  <th style={{ width: 34 }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={sembrableRepos.length === 0}
                      title="Seleccionar todos los tenants"
                    />
                  </th>
                )}
                <th>Aplicación</th>
                <th style={{ textAlign: 'center', width: 240 }}>Versión</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const reg = isRegistered(g.tenantRepo);
                return (
                  <tr key={g.key} className="data-row">
                    {selecting && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(g.tenantRepo)}
                          onChange={() => toggleRow(g.tenantRepo)}
                          disabled={!reg}
                          title={
                            reg
                              ? undefined
                              : `'${g.tenantRepo}' no está en tenants.json — la siembra lo saltaría`
                          }
                        />
                      </td>
                    )}
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <strong>{g.name}</strong>
                        <TenantRegistryBadge
                          known={registeredTenants !== null}
                          registered={reg}
                          repo={g.tenantRepo}
                        />
                      </div>
                      <div className="dim" style={{ fontSize: 11 }}>
                        {g.company}
                        {g.instance ? ` · ${g.instance}` : ''}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <VersionCell app={g.app} target={target} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Flags whether a tenant's derived repo is in the Core's siembra registry. A red
// "no coincide" means the siembra Action would skip it silently — the exact
// trap when the OWL application's company/instance don't match the real repo name.
// Nothing renders when the registry couldn't be read (can't tell → don't alarm).
function TenantRegistryBadge({
  known,
  registered,
  repo,
}: {
  known: boolean;
  registered: boolean;
  repo: string;
}) {
  if (!known || registered) return null;
  return (
    <span
      className="mono"
      style={{
        fontSize: 10,
        color: '#ef4444',
        border: '1px solid #ef4444',
        borderRadius: 4,
        padding: '0 4px',
      }}
      title={`'${repo}' no está en .github/tenants.json del Core. La siembra lo saltaría en silencio. Ajusta company/instance de esta application en OWL para que el repo derivado coincida, o registra el repo.`}
    >
      no coincide
    </span>
  );
}

// A tenant's deployed version: colored drift dot + version. Last-seen rides along
// as a tooltip.
function VersionCell({
  app,
  target,
}: {
  app: ApplicationWithStats;
  target: string | undefined;
}) {
  const tone = versionTone(app.version, target);
  const toneLabel =
    tone === 'match'
      ? 'Al día con el Core'
      : tone === 'drift'
        ? `Desactualizado (Core ${target})`
        : 'Sin datos';
  const seen = app.versionUpdatedAt ? ` · visto ${timeAgo(app.versionUpdatedAt)}` : '';
  const tint = VERSION_TINT[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        border: `1px solid ${tint.border}`,
        background: tint.background,
        borderRadius: 999,
        padding: '3px 10px',
      }}
      title={toneLabel + seen}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: VERSION_DOT[tone],
          flex: '0 0 auto',
        }}
      />
      <span className="mono">{app.version ?? '—'}</span>
    </span>
  );
}

// Inline editor for a product's Core target version (the green/red reference), used
// only when the Core repo can't be read from git.
function CoreCell({
  product,
  value,
  onSave,
}: {
  product: string;
  value: string;
  onSave: (v: string) => void | Promise<void>;
}) {
  const [v, setV] = useState(value);
  useEffect(() => {
    setV(value);
  }, [value]);
  const dirty = v.trim() !== value;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <input
        className="form-input"
        style={{ width: 78, padding: '2px 6px' }}
        value={v}
        placeholder="—"
        maxLength={32}
        aria-label={`Core ${product}`}
        onChange={(e) => setV(e.target.value)}
      />
      <button
        type="button"
        className="cta-secondary"
        style={{ padding: '2px 8px' }}
        disabled={!dirty}
        onClick={() => onSave(v)}
      >
        Update
      </button>
    </div>
  );
}

// The product's Core target: a git badge when the version file was read, a red
// repo error naming what actually went wrong, or the manual KV editor as a
// fallback.
function CoreTarget({
  product,
  siembra,
  manualValue,
  onSave,
}: {
  product: VersionProduct;
  siembra: SiembraState;
  manualValue: string;
  onSave: (v: string) => void | Promise<void>;
}) {
  const { coreVersion, coreVersionStatus } = siembra;
  const source = `${product.versionFile} (${product.versionKey})`;
  if (coreVersion) {
    return (
      <span
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        title={`Leído de ${source} del repo de Core`}
      >
        <span className="dim" style={{ fontSize: 12 }}>
          Core
        </span>
        <span className="mono">{coreVersion}</span>
        <span className="dim" style={{ fontSize: 10 }}>
          · git
        </span>
      </span>
    );
  }
  const repoError =
    coreVersionStatus !== 'ok' &&
    coreVersionStatus !== 'unconfigured' &&
    coreVersionStatus !== undefined;
  if (repoError) {
    // One sentence per cause. A 404 is usually a path that doesn't match, not a
    // permission problem, and being sent to check the token first costs an
    // afternoon of looking in the wrong place.
    const hint =
      coreVersionStatus === 'not_found'
        ? `OWL no encontró ${product.versionFile} en ${siembra.repo}. Revisa la ruta en Administrar productos; si el repo es privado, el token OWL_GITHUB_TOKEN también necesita Contents: Read.`
        : coreVersionStatus === 'key_missing'
          ? `${product.versionFile} existe en ${siembra.repo}, pero no contiene ${product.versionKey}. Ajusta la clave en Administrar productos.`
          : coreVersionStatus === 'unauthorized'
            ? `El token OWL_GITHUB_TOKEN no puede leer ${siembra.repo}: necesita permiso Contents: Read en ese repo.`
            : `OWL no pudo leer ${source} de ${siembra.repo} (${coreVersionStatus}).`;
    const label =
      coreVersionStatus === 'not_found'
        ? '404'
        : coreVersionStatus === 'key_missing'
          ? 'sin clave'
          : coreVersionStatus === 'unauthorized'
            ? '401/403'
            : 'error';
    return (
      <span className="mono" style={{ color: '#ef4444', fontSize: 12 }} title={hint}>
        Core repo {label}
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span className="dim" style={{ fontSize: 12 }}>
        Core
      </span>
      <CoreCell product={product.key} value={manualValue} onSave={onSave} />
    </span>
  );
}

// Drives a product's Siembra Action: shows its Core target, the auto-on-push
// switch, Sembrar todos and the in-list selection flow.
function SiembraBar({
  product,
  siembra,
  manualValue,
  onSaveCore,
  dryRun,
  setDryRun,
  busy,
  running,
  selecting,
  selectedCount,
  onToggleAuto,
  onSembrarTodos,
  onStartSelect,
  onCancelSelect,
  onSembrarSeleccion,
}: {
  product: VersionProduct;
  siembra: SiembraState;
  manualValue: string;
  onSaveCore: (v: string) => void | Promise<void>;
  dryRun: boolean;
  setDryRun: (v: boolean) => void;
  busy: boolean;
  running: boolean;
  selecting: boolean;
  selectedCount: number;
  onToggleAuto: () => void;
  onSembrarTodos: () => void;
  onStartSelect: () => void;
  onCancelSelect: () => void;
  onSembrarSeleccion: () => void;
}) {
  const last = siembra.runs[0];
  // Keep icon + label aligned inside every action button.
  const actionBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6 };

  if (!siembra.configured) {
    return (
      <div style={PANEL_WRAP}>
        <span className="dim" style={{ fontSize: 12 }}>
          Siembra no configurada — define <span className="mono">OWL_GITHUB_TOKEN</span> en el
          entorno de OWL para activarla en <strong>{product.displayName}</strong> (
          <span className="mono">{siembra.repo}</span>).
        </span>
      </div>
    );
  }

  return (
    <div style={PANEL_WRAP}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          rowGap: 10,
          flexWrap: 'wrap',
        }}
      >
        {/* Left zone: what this siembra is and where it reads its target from.
            Basis 0 so the action zone stays centred however long the repo name is. */}
        <div
          style={{
            flex: '1 1 0',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <strong style={{ fontSize: 13 }}>Siembra</strong>
          <CoreTarget
            product={product}
            siembra={siembra}
            manualValue={manualValue}
            onSave={onSaveCore}
          />
          <a
            href={`https://github.com/${siembra.repo}`}
            target="_blank"
            rel="noreferrer"
            className="dim mono"
            style={{
              fontSize: 11,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {siembra.repo}
          </a>
        </div>

        {/* Centre zone: everything that launches a siembra. */}
        <div
          style={{
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
          }}
        >
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <input type="checkbox" checked={siembra.autoEnabled} onChange={onToggleAuto} /> Auto en
            push
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />{' '}
            Dry-run
          </label>
          <button
            type="button"
            className="cta"
            style={actionBtn}
            disabled={running}
            onClick={onSembrarTodos}
          >
            {running && <Spinner />} Sembrar todos
          </button>
          {selecting ? (
            <>
              <button
                type="button"
                className="cta"
                style={actionBtn}
                disabled={running || selectedCount === 0}
                onClick={onSembrarSeleccion}
              >
                {running && <Spinner />} Sembrar selección ({selectedCount})
              </button>
              <button
                type="button"
                className="cta-secondary"
                disabled={busy}
                onClick={onCancelSelect}
              >
                Cancelar
              </button>
            </>
          ) : (
            <button type="button" className="cta-secondary" disabled={busy} onClick={onStartSelect}>
              Seleccionar
            </button>
          )}
        </div>

        {/* Right zone: how the last run went. */}
        <div
          style={{
            flex: '1 1 0',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          {last && <RunStatus run={last} />}
        </div>
      </div>
    </div>
  );
}

// The last siembra run as a tinted pill: green when it landed, red when it broke,
// amber while it is still moving, grey when GitHub skipped it. Links to the run.
function RunStatus({ run }: { run: SiembraRun }) {
  const outcome = run.conclusion ?? run.status ?? 'unknown';
  const inFlight = run.conclusion == null && run.status != null && run.status !== 'completed';
  const tone: VersionTone =
    outcome === 'success'
      ? 'match'
      : outcome === 'failure' || outcome === 'timed_out' || outcome === 'cancelled'
        ? 'drift'
        : 'unknown';
  const dot = inFlight ? RUN_AMBER.dot : VERSION_DOT[tone];
  const tint = inFlight ? RUN_AMBER : VERSION_TINT[tone];
  return (
    <a
      href={run.url}
      target="_blank"
      rel="noreferrer"
      title={`Última siembra: ${outcome} · disparada por ${run.event} · ${new Date(run.createdAt).toLocaleString()}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        maxWidth: '100%',
        border: `1px solid ${tint.border}`,
        background: tint.background,
        borderRadius: 999,
        padding: '3px 10px',
        fontSize: 12,
        color: 'inherit',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: dot,
          flex: '0 0 auto',
        }}
      />
      <span>{outcome}</span>
      <span className="dim">· {run.event}</span>
      <span className="dim">· {timeAgo(run.createdAt)}</span>
    </a>
  );
}

// Admin CRUD for the products under version control. Each change saves immediately
// against its own endpoint (not the theme settings flow), then refreshes the board.
function ProductManager({
  products,
  onClose,
  onChanged,
}: {
  products: VersionProduct[];
  onClose: () => void;
  onChanged: () => Promise<void> | void;
}) {
  const { push } = useToast();
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  useEscapeKey(onClose);

  async function run(action: () => Promise<unknown>, okMsg: string) {
    setBusy(true);
    try {
      await action();
      await onChanged();
      push(okMsg);
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Operación fallida');
    } finally {
      setBusy(false);
    }
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only affordance. The dialog is dismissable from the keyboard via useEscapeKey and the header close button, so the backdrop needs no key handler of its own.
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" style={{ maxWidth: 720 }}>
        <div className="view-head" style={{ marginBottom: 12 }}>
          <div>
            <div className="view-title">Productos de versiones</div>
            <div className="view-sub">
              Repo de Core, workflows y orden por producto (VERA, ARGOS…).
            </div>
          </div>
          <button type="button" className="cta-secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {products.length === 0 && (
            <div className="dim" style={{ fontSize: 12 }}>
              Aún no hay productos. Añade el primero abajo.
            </div>
          )}
          {products.map((p) => (
            <ProductRow
              key={p.id}
              product={p}
              busy={busy}
              onSave={(patch) =>
                run(() => api.updateVersionProduct(p.key, patch), `${p.displayName} guardado`)
              }
              onDelete={async () => {
                const ok = await confirm({
                  title: '¿Eliminar producto?',
                  sub: p.displayName,
                  body: 'Sale del control de versiones. Los repos y despliegues no se tocan.',
                  confirmLabel: 'Eliminar',
                  tone: 'danger',
                });
                if (!ok) return;
                run(() => api.deleteVersionProduct(p.key), `${p.displayName} eliminado`);
              }}
            />
          ))}
        </div>

        <AddProductForm
          busy={busy}
          onCreate={(body) => run(() => api.createVersionProduct(body), `${body.key} creado`)}
        />
      </div>
    </div>
  );
}

const ROW_WRAP: React.CSSProperties = {
  border: '1px solid var(--border-1)',
  borderRadius: 8,
  padding: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const FIELD_ROW: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
  alignItems: 'center',
};

function ProductRow({
  product,
  busy,
  onSave,
  onDelete,
}: {
  product: VersionProduct;
  busy: boolean;
  onSave: (patch: UpdateVersionProduct) => void;
  onDelete: () => void;
}) {
  const [displayName, setDisplayName] = useState(product.displayName);
  const [coreRepo, setCoreRepo] = useState(product.coreRepo);
  const [versionFile, setVersionFile] = useState(product.versionFile);
  const [versionKey, setVersionKey] = useState(product.versionKey);
  const [siembraWorkflow, setSiembraWorkflow] = useState(product.siembraWorkflow);
  const [tenantRepoPattern, setTenantRepoPattern] = useState(product.tenantRepoPattern);
  const [sortOrder, setSortOrder] = useState(String(product.sortOrder));
  const [enabled, setEnabled] = useState(product.enabled);

  const dirty =
    displayName !== product.displayName ||
    coreRepo !== product.coreRepo ||
    versionFile !== product.versionFile ||
    versionKey !== product.versionKey ||
    siembraWorkflow !== product.siembraWorkflow ||
    tenantRepoPattern !== product.tenantRepoPattern ||
    Number(sortOrder) !== product.sortOrder ||
    enabled !== product.enabled;

  return (
    <div style={ROW_WRAP}>
      <div style={FIELD_ROW}>
        <span className="mono" style={{ fontWeight: 600 }}>
          {product.key}
        </span>
        <input
          className="form-input"
          style={{ width: 120 }}
          value={displayName}
          placeholder="Nombre"
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <input
          className="form-input"
          style={{ flex: 1, minWidth: 180 }}
          value={coreRepo}
          placeholder="owner/Core-repo"
          onChange={(e) => setCoreRepo(e.target.value)}
        />
        <button
          type="button"
          className="cta-secondary"
          style={{ marginLeft: 'auto' }}
          disabled={busy}
          onClick={onDelete}
        >
          Eliminar
        </button>
      </div>
      <div style={FIELD_ROW}>
        <label
          className="dim"
          style={{ fontSize: 11 }}
          title="Fichero del Core donde leer la versión objetivo. Python: settings.py · TypeScript: package.json"
        >
          Versión en
          <input
            className="form-input"
            style={{ width: 130, marginLeft: 6 }}
            value={versionFile}
            placeholder="settings.py"
            onChange={(e) => setVersionFile(e.target.value)}
          />
        </label>
        <label
          className="dim"
          style={{ fontSize: 11 }}
          title="Constante o clave que contiene la versión: RUNTIME_VERSION, version…"
        >
          Clave
          <input
            className="form-input"
            style={{ width: 130, marginLeft: 6 }}
            value={versionKey}
            placeholder="RUNTIME_VERSION"
            onChange={(e) => setVersionKey(e.target.value)}
          />
        </label>
        <label className="dim" style={{ fontSize: 11 }}>
          Workflow
          <input
            className="form-input"
            style={{ width: 120, marginLeft: 6 }}
            value={siembraWorkflow}
            onChange={(e) => setSiembraWorkflow(e.target.value)}
          />
        </label>
        <label className="dim" style={{ fontSize: 11 }}>
          Patrón tenant
          <input
            className="form-input"
            style={{ width: 220, marginLeft: 6 }}
            value={tenantRepoPattern}
            onChange={(e) => setTenantRepoPattern(e.target.value)}
          />
        </label>
        <label className="dim" style={{ fontSize: 11 }}>
          Orden
          <input
            className="form-input"
            style={{ width: 56, marginLeft: 6 }}
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />{' '}
          Activo
        </label>
        <button
          type="button"
          className="cta"
          style={{ marginLeft: 'auto' }}
          disabled={busy || !dirty}
          onClick={() =>
            onSave({
              displayName,
              coreRepo,
              versionFile,
              versionKey,
              siembraWorkflow,
              tenantRepoPattern,
              sortOrder: Number(sortOrder),
              enabled,
            })
          }
        >
          Guardar
        </button>
      </div>
    </div>
  );
}

function AddProductForm({
  busy,
  onCreate,
}: {
  busy: boolean;
  onCreate: (body: CreateVersionProduct) => void;
}) {
  const [key, setKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [coreRepo, setCoreRepo] = useState('');
  const valid = key.trim() !== '' && displayName.trim() !== '' && coreRepo.trim() !== '';

  return (
    <div style={{ ...ROW_WRAP, marginTop: 14, borderStyle: 'dashed' }}>
      <div className="label-xs">Nuevo producto</div>
      <div style={FIELD_ROW}>
        <input
          className="form-input"
          style={{ width: 110 }}
          value={key}
          placeholder="KEY (ARGOS)"
          onChange={(e) => setKey(e.target.value.toUpperCase())}
        />
        <input
          className="form-input"
          style={{ width: 130 }}
          value={displayName}
          placeholder="Nombre"
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <input
          className="form-input"
          style={{ flex: 1, minWidth: 180 }}
          value={coreRepo}
          placeholder="owner/ARGOS-Core"
          onChange={(e) => setCoreRepo(e.target.value)}
        />
        <button
          type="button"
          className="cta"
          disabled={busy || !valid}
          onClick={() => {
            onCreate({
              key: key.trim(),
              displayName: displayName.trim(),
              coreRepo: coreRepo.trim(),
            });
            setKey('');
            setDisplayName('');
            setCoreRepo('');
          }}
        >
          Crear
        </button>
      </div>
    </div>
  );
}
